import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button } from '@avangard/ui/core';

import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { UpdateSchoolRequest } from '@modules/schools/requests';
import { DialogSchoolDelete } from '@modules/schools/moleculas';
import { UpdateSchoolMutation, RemoveSchoolMutation } from '@modules/schools/graphql';
import { SchoolSummaryEditFormGroup } from './school-summary-edit-form-group';
import { SchoolAccessFormGroup } from './school-access-form-group';
import { SchoolStatsFormGroup } from './school-stats-form-group';

import type { SchoolEntity } from '@modules/schools/entities';
import type {
    UpdateSchoolMutationType,
    UpdateSchoolMutationVariables,
    RemoveSchoolMutationType,
    RemoveSchoolMutationVariables,
} from '@modules/types/graphql';

export enum SchoolEditFormTypesEnum {
    summary = 'summary',
    access = 'access',
    stats = 'stats',
}

type SchoolEditFormProps = {
    canEditSchool: boolean;
    canDeleteSchool: boolean;
    canViewAccess: boolean;
    canCreateAccess: boolean;
    canEditAccess: boolean;
    canDeleteAccess: boolean;
    canSendAccess: boolean;
    type: SchoolEditFormTypesEnum;
    school: SchoolEntity;
    onDeleteSchool: () => void;
};

const StyledForm = styled(Form)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
`;

const SchoolEditForm = (props: SchoolEditFormProps): React.ReactElement => {
    const {
        canEditSchool,
        canDeleteSchool,
        canViewAccess,
        canCreateAccess,
        canEditAccess,
        canDeleteAccess,
        canSendAccess,
        type,
        school,
        onDeleteSchool,
    } = props;

    const { t } = useTranslation(['common', 'errors', 'schools']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [updateSchool] = useMutation<UpdateSchoolMutationType, UpdateSchoolMutationVariables>(
        UpdateSchoolMutation,
    );

    const [removeSchool, { loading: removeSchoolLoading }] = useMutation<
        RemoveSchoolMutationType,
        RemoveSchoolMutationVariables
    >(RemoveSchoolMutation);

    const initialValues = {
        ...school,
        region: school.region,
        location: school.location,
    };

    const renderedFormComponent = React.useMemo(() => {
        switch (type) {
            case SchoolEditFormTypesEnum.summary:
                return <SchoolSummaryEditFormGroup school={school} />;

            case SchoolEditFormTypesEnum.stats:
                return <SchoolStatsFormGroup school={school} />;

            case SchoolEditFormTypesEnum.access:
                return (
                    <SchoolAccessFormGroup
                        school={school}
                        canViewAccess={canViewAccess}
                        canCreateAccess={canCreateAccess}
                        canEditAccess={canEditAccess}
                        canDeleteAccess={canDeleteAccess}
                        canSendAccess={canSendAccess}
                    />
                );

            default:
                return null;
        }
    }, [
        school,
        type,
        canViewAccess,
        canCreateAccess,
        canEditAccess,
        canDeleteAccess,
        canSendAccess,
    ]);

    const handleClickRemoveSchool = async (): Promise<void> => {
        try {
            const { data: removeSchoolData } = await removeSchool({
                variables: { id: school.id },
            });

            if (removeSchoolData?.removeSchool) {
                enqueueSuccess(t('schools:notifiers.remove_school.success'));
                onDeleteSchool();
            } else {
                enqueueError(t('schools:notifiers.remove_school.error'));
            }
        } catch (e) {
            enqueueError(e);
        }
    };

    return (
        <ExtendedFormik
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={yup.object({
                location: yup.mixed().required(t('errors:validations.required') ?? ''),
                type: yup.string().required(t('errors:validations.required') ?? ''),
                shortName: yup.string().required(t('errors:validations.required') ?? ''),
                email: yup.string().required(t('errors:validations.required') ?? ''),
            })}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    const item = new UpdateSchoolRequest(values);

                    const { data: updateSchoolData } = await updateSchool({
                        variables: { id: school.id, item },
                    });

                    if (updateSchoolData?.updateSchool?.id) {
                        enqueueSuccess(t('schools:notifiers.edit_school.success'));
                    } else {
                        enqueueError(t('schools:notifiers.edit_school.error'));
                    }
                } catch (e) {
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { isSubmitting } = formikProps;

                const isDisabledSubmit = isSubmitting || removeSchoolLoading;

                return (
                    <StyledForm>
                        <FormStyled.FormColumnList>
                            {renderedFormComponent}

                            {type === SchoolEditFormTypesEnum.summary ? (
                                <FormStyled.FormColumn>
                                    <FormStyled.FormRowSubmit>
                                        <Button
                                            type='submit'
                                            disabled={!canEditSchool}
                                            loading={isDisabledSubmit}
                                        >
                                            {t('common:actions.save')}
                                        </Button>

                                        {false && canDeleteSchool ? (
                                            <DialogSchoolDelete
                                                loading={isDisabledSubmit}
                                                onAccept={handleClickRemoveSchool}
                                            />
                                        ) : null}
                                    </FormStyled.FormRowSubmit>
                                </FormStyled.FormColumn>
                            ) : null}
                        </FormStyled.FormColumnList>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { SchoolEditForm };
